import React, { createContext, useContext, useState, useCallback } from 'react';

const EnhancedToastContext = createContext();

export const useToast = () => {
  const context = useContext(EnhancedToastContext);
  if (!context) {
    throw new Error('useToast must be used within an EnhancedToastProvider');
  }
  return context;
};

export const useEnhancedToast = useToast; // Alias for backward compatibility

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);
  const [counter, setCounter] = useState(0);

  const removeToast = useCallback((id) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);

  const addToast = useCallback((message, type = 'info', options = {}) => {
    // Generate unique ID using timestamp + counter + random
    const uniqueId = `${Date.now()}-${counter}-${Math.random().toString(36).substr(2, 9)}`;
    setCounter(prev => prev + 1);
    const {
      duration = 5000,
      persistent = false,
      action = null,
      position = 'top-right'
    } = options;

    const toast = {
      id: uniqueId,
      message,
      type,
      duration,
      persistent,
      action,
      position,
      timestamp: new Date()
    };

    setToasts(prev => [...prev, toast]);

    if (duration > 0 && !persistent) {
      setTimeout(() => {
        setToasts(prev => prev.filter(toast => toast.id !== uniqueId));
      }, duration);
    }

    return uniqueId;
  }, [counter]);

  const removeAllToasts = useCallback(() => {
    setToasts([]);
  }, []);

  const updateToast = useCallback((id, updates) => {
    setToasts(prev => prev.map(toast =>
      toast.id === id ? { ...toast, ...updates } : toast
    ));
  }, []);

  const value = {
    toasts,
    addToast,
    removeToast,
    removeAllToasts,
    updateToast,
    success: (message, options) => addToast(message, 'success', options),
    error: (message, options) => addToast(message, 'error', options),
    warning: (message, options) => addToast(message, 'warning', options),
    info: (message, options) => addToast(message, 'info', options),
    loading: (message, options) => addToast(message, 'loading', { ...options, persistent: true }),
  };

  return (
    <EnhancedToastContext.Provider value={value}>
      {children}
      <EnhancedToastContainer
        toasts={toasts}
        onRemove={removeToast}
        onUpdate={updateToast}
      />
    </EnhancedToastContext.Provider>
  );
};

const EnhancedToastContainer = ({ toasts, onRemove, onUpdate }) => {
  if (toasts.length === 0) return null;

  const getToastIcon = (type) => {
    switch (type) {
      case 'success':
        return '✓';
      case 'error':
        return '✗';
      case 'warning':
        return '⚠';
      case 'loading':
        return '⟳';
      default:
        return 'ℹ';
    }
  };

  const getToastStyles = (type) => {
    const baseStyles = 'p-4 rounded-lg shadow-lg transition-all duration-300 max-w-sm border-l-4';

    switch (type) {
      case 'success':
        return `${baseStyles} bg-green-50 border-green-500 text-green-800`;
      case 'error':
        return `${baseStyles} bg-red-50 border-red-500 text-red-800`;
      case 'warning':
        return `${baseStyles} bg-yellow-50 border-yellow-500 text-yellow-800`;
      case 'loading':
        return `${baseStyles} bg-blue-50 border-blue-500 text-blue-800`;
      default:
        return `${baseStyles} bg-blue-50 border-blue-500 text-blue-800`;
    }
  };

  return (
    <div className="fixed top-4 right-4 z-50 space-y-2">
      {toasts.map(toast => (
        <div
          key={toast.id}
          className={getToastStyles(toast.type)}
        >
          <div className="flex items-start justify-between">
            <div className="flex items-start space-x-2">
              <span className="text-lg">{getToastIcon(toast.type)}</span>
              <div className="flex-1">
                <p className="font-medium">{toast.message}</p>
                {toast.action && (
                  <button
                    onClick={toast.action.onClick}
                    className="mt-2 text-sm font-medium underline hover:no-underline"
                  >
                    {toast.action.label}
                  </button>
                )}
              </div>
            </div>
            {!toast.persistent && (
              <button
                onClick={() => onRemove(toast.id)}
                className="ml-2 text-gray-400 hover:text-gray-600"
              >
                ×
              </button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};
